import React, { useEffect, useState } from 'react';
import ToastAlert, { InlineAlert } from '@saucelabs/ds-alert';
import { constants as sentryConstants, Sentry } from '@saucelabs/sl-util-sentry';

import { BuildStatus } from '../../graphql/__generated__/graphql';

enum ErrorCodes {
  TIMEOUT = 'TIMEOUT',
  DIFF_TIMEOUT = 'DIFF_TIMEOUT',
}

const checkIsErrorCode = (code: string): code is ErrorCodes => Object
  .keys(ErrorCodes)
  .includes(code);

type Props = {
  status: BuildStatus,
  error?: Record<'code', ErrorCodes>
}

const DiffHeaderStatus: React.FC<Props> = ({ status, error }) => {
  const [showSuccess, setShowSuccess] = useState(false);
  const isRunning = status === BuildStatus.Running;
  const isErrored = status === BuildStatus.Errored;
  const isSuccess = status === BuildStatus.Equal || status === BuildStatus.Unapproved;

  const errorTexts: Record<ErrorCodes, string> = {
    TIMEOUT: 'Visual build was not completed within the time limit',
    DIFF_TIMEOUT: 'Visual snapshot was not completed within the time limit',
  };

  const isErrorCode = !!error?.code && checkIsErrorCode(error.code);
  let errorMsg = '';
  if (isErrored) {
    errorMsg = isErrorCode ? errorTexts[error.code] : 'Unknown error';
  }

  useEffect(() => {
    if (isRunning) {
      setShowSuccess(true);
    } else if (!isSuccess) {
      setShowSuccess(false);
    }
  }, [status]);

  useEffect(() => {
    if (!isErrorCode) {
      Sentry.withScope((scope) => {
        scope.setTag('owner', sentryConstants.TEAMS.VISUAL);
        Sentry.captureException(new Error('Unhandled build error', { cause: error }));
      });
    }
  }, [error]);

  return (
    <>
      {isRunning && (
        <div className="flex-1 p-2 pt-0">
          <InlineAlert
            color="info"
            title="This build is executing in real time"
          >
            Snapshots will appear here as the build progresses.
            We&apos;ll reload the page automatically if the build status changes.
          </InlineAlert>
        </div>
      )}
      {isErrored && (
        <div className="flex-1 p-2 pt-0">
          <InlineAlert
            color="warning"
            title={errorMsg}
          >
            Contact support or try again later.
          </InlineAlert>
        </div>
      )}
      {(isSuccess && showSuccess) && (
        <div className="fixed top-5 z-1000 sm:w-50 left-0 right-0 mx-auto px-2">
          <ToastAlert
            color="success"
            onDismiss={() => setShowSuccess(false)}
            title="Build is now completed"
          >
            You can review the snapshots
          </ToastAlert>
        </div>
      )}
    </>
  );
};

export default DiffHeaderStatus;
